import { ICommonSettings, IGoogleAuthSetting } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import _ from "lodash";
import * as React from "react";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigGoogleAuthSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingGoogleAuth?: IGoogleAuthSetting;
}

export interface IConfigGoogleAuthSettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingGoogleAuthId: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigGoogleAuthSettings extends React.PureComponent<
  IConfigGoogleAuthSettingsProps,
  IConfigGoogleAuthSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigGoogleAuthSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle",
      editingGoogleAuth: undefined
    };
  }

  componentDidMount() {
    this.loadAuthSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigGoogleAuthSettingsProps) {
    if (prevProps.editingGoogleAuthId && !this.props.editingGoogleAuthId) {
      this.formRef.current?.resetFields();
    }
    if (prevProps.editingGoogleAuthId !== this.props.editingGoogleAuthId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadAuthSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadAuthSetting();
    }
  }

  loadAuthSetting = () => {
    const authSettings = _.cloneDeep(this.props.commonSettings?.googleAuth);

    if (this.props.editingGoogleAuthId) {
      const auth = authSettings?.find(
        (x) => x.id === this.props.editingGoogleAuthId && !x.isGlobalCommonConfigOnProjectConfig
      );
      this.setState({ editingGoogleAuth: auth }, () => {
        if (auth) {
          const fieldsValue = this.formRef.current?.getFieldsValue();
          this.formRef.current?.resetFields();

          fieldsValue.googleAuthName = auth.name;
          fieldsValue.clientId = auth.clientId;
          fieldsValue.clientSecret = auth.clientSecret;
          fieldsValue.serverClientId = auth.serverClientId;
          fieldsValue.serverClientSecret = auth.serverClientSecret;
          fieldsValue.iosUrlScheme = auth.iosUrlScheme;

          this.formRef.current?.setFieldsValue(fieldsValue);
        }
      });
    }
  };

  existingNameValidator = async (_rule, value) => {
    const authsSettings = this.props.commonSettings?.googleAuth;
    if (!authsSettings) return;

    const isSettingAlreadyExist = authsSettings.some(
      (x) => x.name === value && x.id !== this.props.editingGoogleAuthId && !x.isGlobalCommonConfigOnProjectConfig
    );

    if (isSettingAlreadyExist) throw new Error("Google Auth Settings Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();

    const obj: IGoogleAuthSetting = {
      id:
        this.props.editingGoogleAuthId && this.props.editingGoogleAuthId !== ""
          ? this.props.editingGoogleAuthId
          : Guid.create().toString(),
      name: data.googleAuthName,
      clientId: data.clientId?.trim(),
      clientSecret: data.clientSecret?.trim(),
      iosUrlScheme: data.iosUrlScheme?.trim(),
      serverClientId: data.serverClientId?.trim(),
      serverClientSecret: data.serverClientSecret?.trim()
    };

    if (this.props.editingGoogleAuthId) {
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleAuth",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "googleAuth",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteAuth = () => {
    if (!this.props.editingGoogleAuthId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingGoogleAuthId || "",
      configKey: "googleAuth",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`GoogleAuth setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Google Login settings"
        secondHeader="Google Login settings"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingGoogleAuthId ? this.deleteAuth : undefined}
            deleteTestId="googleAuthSettingDeleteButton"
            submitTestId="googleAuthSettingCreateButton"
            submitLabel={this.props.editingGoogleAuthId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configGoogleLoginForm"
          />
        }
      >
        <Form
          id="configGoogleLoginForm"
          name="authSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody
            description={this.props.getSectionDescription("googleloginsettingspopup_googleloginsettings_description")}
          >
            <ConfigField label="Name" required tooltipContentKey="googleloginsettingspopup_googleloginsettings_name">
              <Form.Item
                name="googleAuthName"
                rules={[
                  { required: true, message: "Please enter a Google Auth Settings Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Google Auth Settings name is invalid." },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="googleAuthSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Client ID"
              required
              tooltipContentKey="googleloginsettingspopup_googleloginsettings_clientid"
            >
              <Form.Item name="clientId" rules={[{ required: true, message: "Please enter a Client ID." }]}>
                <KuikaInput size="md" data-testid="googleAuthSettingClientIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Client Secret"
              required
              tooltipContentKey="googleloginsettingspopup_googleloginsettings_clientsecret"
            >
              <Form.Item name="clientSecret" rules={[{ required: true, message: "Please enter a Client Secret." }]}>
                <KuikaInput size="md" data-testid="googleAuthSettingClientSecretInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Server Client ID"
              required
              tooltipContentKey="googleloginsettingspopup_googleloginsettings_serverclientid"
            >
              <Form.Item
                name="serverClientId"
                rules={[{ required: true, message: "Please enter a Server Client Id." }]}
              >
                <KuikaInput size="md" data-testid="googleAuthSettingServerClientIdInput" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="Server Client Secret"
              tooltipContentKey="googleloginsettingspopup_googleloginsettings_serverclientsecret"
            >
              <Form.Item name="serverClientSecret">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
            <ConfigField
              label="IOS URL Scheme"
              tooltipContentKey="googleloginsettingspopup_googleloginsettings_iosurlscheme"
            >
              <Form.Item name="iosUrlScheme">
                <KuikaInput size="md" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
